import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { deepEquals } from '@jack-henry/frontend-utils/functions';
import { NavigationService } from '@treasury/core/navigation';
import { DownloadService } from '@treasury/domain/download';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { Feature } from '@treasury/domain/services/feature-flags';
import {
    Wire,
    WireAccount,
    WireCompany,
    WireFile,
    WireIsoService,
    WiresService,
} from '@treasury/domain/wires';
// eslint-disable-next-line no-duplicate-imports
import type {
    WireFileFilter,
    WireIsoLabels,
    WiresFilter,
    WireUploadFormat,
} from '@treasury/domain/wires';
import { TmContainer, TmFooterService } from '@treasury/presentation';
import {
    cardsIcon,
    circlePlusIcon,
    squaresUser,
    templateIcon,
} from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-action-button';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-download';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
import '@treasury/presentation/components/tm-tabs';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { css, html, nothing, PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import '../partials/wire-file-filter-sheet';
import '../partials/wire-file-table';
import '../partials/wire-payments-filter-sheet';
import '../partials/wire-payments-table';
import '../partials/wire-upload-formats-table';
import '../partials/wires-container-sidebar';
import '../partials/wires-container-tabs';

export const tagName = 'wires-container';
@customElement(tagName)
export class WiresContainer extends TmContainer {
    @InjectProperty()
    declare private readonly headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly wireService: WiresService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @InjectProperty()
    declare private readonly footerService: TmFooterService;

    @InjectProperty()
    declare private dialogService: TmDialogService;

    @InjectProperty()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    declare private readonly downloadService: DownloadService<any>;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @state()
    private wirePayments: Wire[] = [];

    @state()
    private selectedWires: Wire[] = [];

    @state()
    private recurringWirePayments: Wire[] = [];

    @state()
    private wireFileActivity: WireFile[] = [];

    @state()
    private wireFileFormats: WireUploadFormat[] = [];

    @state()
    private domesticEntitlement = false;

    @state()
    private internationalEntitlement = false;

    @state()
    private bottomSheetOpen = false;

    @state()
    private filterSheetOpen = false;

    @state()
    private activeTab = '';

    @state()
    private tabs = [
        { label: 'One Time', id: 'OneTime', enabled: true },
        { label: 'Recurring', id: 'Recurring', enabled: true },
    ];

    @state()
    private wireCompanies: Array<WireCompany> = [];

    @state()
    private debitWireAccounts: Array<WireAccount> = [];

    @state()
    private oneTimeWireCount = 0;

    @state()
    private recurringWireCount = 0;

    @state()
    private wireFileCount = 0;

    @state()
    private loadingResults = false;

    @state()
    private wiresFeatureFlagEnabled = false;

    @state()
    private wireTemplatesFeatureFlagEnabled = false;

    @state()
    private wireFileFeatureFlagEnabled = false;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    @state()
    private filterState?: WiresFilter | WireFileFilter;

    @state()
    private comments = '';

    @state()
    private oneTimeTotals?: { totalAmount: number; count: number; status: string }[];

    @state()
    private recurringTotals?: { totalAmount: number; count: number; status: string }[];

    @state()
    private isRecurring = false;

    @state()
    private wirePendingProcessFlagEnabled = true;

    @state()
    private hasWireFileUploadFeatureFlagEnabled = false;

    private oneTimeFilters?: WiresFilter;

    private recurringFilters?: WiresFilter;

    private wireFileFilters?: WireFileFilter;

    private summary?: { totalAmount: number; count: number; status: string }[];

    private get activeFilters() {
        return this.isRecurring ? this.recurringFilters : this.oneTimeFilters;
    }

    private get hasWireEntitlement() {
        return this.domesticEntitlement || this.internationalEntitlement;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.headerService.configure({
            title: 'Wires',
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.Wires}
                        .isRecurring=${this.isRecurring}
                        .filterModel=${this.isRecurring
                            ? this.recurringFilters
                            : this.oneTimeFilters}
                        .disabled=${true}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
        });
        this.loading = true;
    }

    public disconnectedCallback() {
        super.disconnectedCallback?.();
        this.footerService.hide();
    }

    protected render() {
        return html`${this.renderLoader()}
            <tm-sidebar>
                <div
                    class=${classMap({
                        'flex-grow flex flex-col h-full': true,
                        'py-2': this.isDesktop,
                    })}
                    slot="main-content"
                >
                    <div class="flex-none">
                        ${this.isDesktop ? nothing : this.renderActionItems()}
                    </div>
                    <div class="flex-grow">
                        <tm-section
                            class=${classMap({
                                'flex flex-col h-full': true,
                                'overflow-hidden bg-page': !this.isDesktop,
                                'mx-2 pb-2 rounded-lg': this.isDesktop,
                            })}
                        >
                            <div class="flex flex-col h-full">
                                <div
                                    class=${classMap({
                                        'flex justify-between items-center': true,
                                        'bg-primary': !this.isDesktop,
                                    })}
                                >
                                    ${this.isDesktop
                                        ? html`<wires-container-tabs
                                              .activeTab=${this.activeTab}
                                              @switchTab=${({ detail }: CustomEvent) => {
                                                  this.activeTab = detail.activeTab;
                                                  this.isRecurring = this.activeTab === 'Recurring';
                                              }}
                                          ></wires-container-tabs>`
                                        : this.renderTabs()}
                                    ${this.renderCreateButton()}
                                </div>
                                <div class="flex-grow">${this.renderTable()}</div>
                            </div>
                        </tm-section>
                    </div>
                </div>
                <wires-container-sidebar slot="sidebar-content"></wires-container-sidebar>
            </tm-sidebar>
            ${this.renderFilterSheets()}`;
    }

    protected renderCreateButton() {
        switch (this.activeTab) {
            case 'OneTime':
            case 'Recurring':
                return this.isDesktop && this.hasWireEntitlement
                    ? html`<tm-button
                          importance="primary"
                          .icon=${circlePlusIcon}
                          class="mr-4"
                          @click=${() => {
                              this.navService.navigate(
                                  this.wiresFeatureFlagEnabled
                                      ? `/wires/domestic/select/new/usd`
                                      : `/wires/templates/initiate`,
                                  {
                                      backToPaymentsHub: true,
                                  }
                              );
                          }}
                          >${this.wiresFeatureFlagEnabled
                              ? 'Create new wire'
                              : 'Create wire from template'}</tm-button
                      >`
                    : nothing;
            case 'WireFileFormats':
                return html`<tm-button
                    importance="primary"
                    .icon=${circlePlusIcon}
                    class="mr-4"
                    @click=${() => {
                        this.navService.navigate(`wires/files/new-format`);
                    }}
                    >Create new format</tm-button
                >`;
            default:
                return nothing;
        }
    }

    protected async firstUpdated() {
        const { params } = await this.navService.getRouteData<{
            filter: WiresFilter | WireFileFilter;
            tab: string;
        }>();
        this.activeTab = params.tab || 'OneTime';
        this.filterState = params.filter;

        this.wiresFeatureFlagEnabled = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaCreateSingleWireManual);
        this.wireTemplatesFeatureFlagEnabled = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaWireTemplatesList);
        this.wireFileFeatureFlagEnabled = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaWireFileActivity);
        this.hasWireFileUploadFeatureFlagEnabled = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaWireFileUpload);
        this.wirePendingProcessFlagEnabled = await this.entitlementsService.isEnabled(
            Entitlements.HasWirePendingProcessStatus
        );

        await this.tryFetch(
            async () =>
                Promise.all([
                    this.entitlementsService.getEntitlements(),
                    this.wireService.getWireCompanies(),
                    this.wireService.fetchTransferAccounts(),
                    this.wireIsoService.getLabels(),
                ]),
            ([entitlements, wireCompanies, debitWireAccounts, wireIsoLabels]) => {
                this.domesticEntitlement = entitlements.get('Wire, Domestic, Create Wire') || false;
                this.internationalEntitlement =
                    entitlements.get('Wire, International, Create Wire') || false;
                this.wireCompanies = wireCompanies;
                this.debitWireAccounts = debitWireAccounts as unknown as WireAccount[];
                this.wireIsoLabels = wireIsoLabels;
            }
        );

        this.configureFooter();
    }

    protected async updated(changedProps: PropertyValues) {
        if (changedProps.has('activeTab') || changedProps.has('wirePayments')) {
            const paymentCount = this.isRecurring ? this.recurringWireCount : this.oneTimeWireCount;
            this.downloadService.updateConfig({
                filterModel: this.isRecurring ? this.recurringFilters : this.oneTimeFilters,
                isRecurring: this.isRecurring,
                disabled: paymentCount === 0,
            });
        }
        if (changedProps.has('activeTab') || changedProps.has('selectedWires')) {
            this.configureFooter();
        }
        if (changedProps.has('activeTab') && this.activeTab === 'Creditors') {
            this.navService.navigate(`/wires/creditors`);
        }
        if (changedProps.has('activeTab') && this.activeTab === 'Templates') {
            this.navService.navigate(`/wires/templates/view`);
        }
        if (changedProps.has('activeTab') && this.activeTab === 'WireFileFormats') {
            await this.fetchWireFileFormats();
        }
    }

    private renderTabs() {
        const enabledTabs = this.tabs.filter(tab => tab.enabled);
        return html`<tm-tabs
            .tabs=${enabledTabs}
            .activeTab=${this.activeTab}
            @switchTab=${(e: CustomEvent) => {
                this.activeTab = e.detail.activeTab;
                this.isRecurring = this.activeTab === 'Recurring';
            }}
        ></tm-tabs>`;
    }

    private renderTable() {
        switch (this.activeTab) {
            case 'OneTime':
            case 'Recurring':
                return this.isDesktop
                    ? html`<wire-payments-table
                          class="h-full"
                          .loading=${this.loadingResults}
                          .isRecurring=${this.isRecurring}
                          .totals=${this.isRecurring ? this.recurringTotals : this.oneTimeTotals}
                          .wirePayments=${this.activeTab === 'OneTime'
                              ? this.wirePayments
                              : this.recurringWirePayments}
                          @filter-wire-payments=${() => {
                              this.filterSheetOpen = true;
                          }}
                          @selection-changed=${(e: CustomEvent) => {
                              this.selectedWires = e.detail;
                          }}
                          @wire-select=${({ detail }: CustomEvent) => {
                              this.navService.navigate(`/wires/${detail.id}`, {
                                  filter: this.isRecurring
                                      ? this.recurringFilters
                                      : this.oneTimeFilters,
                              });
                          }}
                      ></wire-payments-table>`
                    : html`<wire-payments-table
                          class="h-full"
                          .loading=${this.loadingResults}
                          .isRecurring=${this.isRecurring}
                          .wirePayments=${this.activeTab === 'OneTime'
                              ? this.wirePayments
                              : this.recurringWirePayments}
                          @filter-wire-payments=${() => {
                              this.filterSheetOpen = true;
                          }}
                          @wire-select=${({ detail }: CustomEvent) => {
                              this.navService.navigate(`/wires/${detail.id}`);
                          }}
                      ></wire-payments-table>`;
            case 'WireFile':
                return html`<wire-file-table
                    class="h-full"
                    .loading=${this.loadingResults}
                    .isDesktop=${this.deviceService.isDesktop}
                    .wireFileActivity=${this.wireFileActivity}
                    @filter-wire-files=${() => {
                        this.filterSheetOpen = true;
                    }}
                    @wire-file-select=${({ detail }: CustomEvent) => {
                        this.selectWireFile(detail.wireFileId);
                    }}
                ></wire-file-table>`;
            case 'WireFileFormats':
                return html`<wire-upload-formats-table
                    class="h-full"
                    .wireFileFormats=${this.wireFileFormats}
                    @wire-file-selected=${(e: CustomEvent) => {
                        const format: WireUploadFormat = e.detail.wireFile;
                        this.navService.navigate(`wires/files/${format.id}`);
                    }}
                    @wire-upload-format-deleted=${() => {
                        this.fetchWireFileFormats();
                    }}
                ></wire-upload-formats-table>`;
            default:
                return nothing;
        }
    }

    private async selectWireFile(wireFileId: number) {
        if (this.oneTimeFilters) {
            this.oneTimeFilters.valueDateEnd = undefined;
            this.oneTimeFilters.valueDateStart = undefined;
            this.oneTimeFilters.wireFileId = wireFileId;
        }
        this.fetchWirePayments('OneTime', this.oneTimeFilters as WiresFilter);
        this.activeTab = 'OneTime';
    }

    private async fetchWirePayments(selectedTab: 'OneTime' | 'Recurring', filters: WiresFilter) {
        this.loadingResults = true;
        try {
            if (this.filterState && this.activeTab === selectedTab) {
                filters = this.filterState as WiresFilter;
                this.filterState = undefined;
            }
            const result = await this.wireService.fetchWireActivityWithTotals(
                filters as WiresFilter,
                {
                    type: selectedTab,
                }
            );
            if (selectedTab === 'OneTime') {
                this.wirePayments = result.data ?? [];
                this.oneTimeTotals = result.totals;
                this.oneTimeWireCount = this.wirePayments.length;
                this.oneTimeFilters = filters as WiresFilter;
            } else {
                this.recurringWirePayments = result.data ?? [];
                this.recurringTotals = result.totals;
                this.recurringWireCount = this.recurringWirePayments.length;
                this.recurringFilters = filters as WiresFilter;
            }

            this.summary = result.totals;

            this.updateTabs();
        } finally {
            this.loadingResults = false;
        }
    }

    private async fetchWireFileActivity(filter: WireFileFilter) {
        this.loadingResults = true;
        try {
            if (this.filterState && this.activeTab === 'WireFile') {
                filter = this.filterState as WireFileFilter;
                this.filterState = undefined;
            }

            this.wireFileActivity = (await this.wireService.fetchWireFileActivity(
                filter as WireFileFilter
            )) as WireFile[];
            this.wireFileCount = this.wireFileActivity.length;
            this.wireFileFilters = filter as WireFileFilter;
            this.updateTabs();
        } finally {
            this.loadingResults = false;
        }
    }

    private async fetchWireFileFormats() {
        this.loadingResults = true;
        this.wireFileFormats = await this.wireService.fetchWireUploadFormats();
        this.loadingResults = false;
    }

    private updateTabs() {
        if (!this.isDesktop) {
            this.tabs.map(tab => {
                if (tab.id === 'OneTime') {
                    tab.label = `One Time (${this.oneTimeWireCount})`;
                }
                if (tab.id === 'Recurring') {
                    tab.label = `Recurring (${this.recurringWireCount})`;
                }
            });
        }
    }

    private renderActionItems() {
        const paymentAction =
            this.wiresFeatureFlagEnabled && this.hasWireEntitlement
                ? html`<tm-action-button
                      class="my-2"
                      .buttonTitle=${'Create Wire'}
                      @click=${() => {
                          this.navService.navigate(`/wires/domestic/select/new/usd`);
                      }}
                  ></tm-action-button>`
                : nothing;
        const createFromTemplateAction =
            this.hasWireEntitlement && !this.wiresFeatureFlagEnabled
                ? html`<tm-action-button
                      class="my-2"
                      .icon=${templateIcon}
                      .buttonTitle=${'Create Wire from Template'}
                      .buttonDescription=${'Create USD wire payment(s) from template'}
                      @click=${() => {
                          this.navService.navigate('/wires/templates/initiate', {
                              backToPaymentsHub: true,
                          });
                      }}
                  ></tm-action-button>`
                : nothing;
        const creditorsAction = html`<tm-action-button
            class="my-2"
            .icon=${squaresUser}
            .buttonTitle=${this.wireIsoLabels.creditors}
            @click=${() => {
                this.navService.navigate('/wires/creditors');
            }}
        ></tm-action-button>`;
        const templatesAction =
            this.wireTemplatesFeatureFlagEnabled && this.hasWireEntitlement
                ? html`<tm-action-button
                      class="my-2"
                      .icon=${cardsIcon}
                      .buttonTitle=${'Templates'}
                      @click=${() => {
                          this.navService.navigate('/wires/templates/view');
                      }}
                  ></tm-action-button>`
                : nothing;
        const fileUploadAction = this.hasWireFileUploadFeatureFlagEnabled
            ? html`<tm-action-button
                  class="my-2"
                  .buttonTitle=${'Wire Files'}
                  @click=${() => {
                      this.navService.navigate('/wires/files');
                  }}
              ></tm-action-button>`
            : nothing;
        return [
            paymentAction,
            createFromTemplateAction,
            creditorsAction,
            templatesAction,
            fileUploadAction,
        ];
    }

    private renderFilterSheets() {
        return html` <wire-payments-filter-sheet
                class=${classMap({ hidden: this.activeTab !== 'OneTime' })}
                .open=${this.filterSheetOpen}
                .hasWirePendingProcessStatus=${this.wirePendingProcessFlagEnabled}
                .loading=${this.loading}
                .wireCompanies=${this.wireCompanies}
                .debitAccounts=${this.debitWireAccounts as WireAccount[]}
                @close=${() => {
                    this.filterSheetOpen = false;
                }}
                @applyFilters=${({ detail }: CustomEvent) => {
                    const { oneTimeDto } = detail;

                    if (!deepEquals(this.oneTimeFilters, oneTimeDto)) {
                        this.fetchWirePayments('OneTime', oneTimeDto);
                    }
                }}
            ></wire-payments-filter-sheet>

            <wire-payments-filter-sheet
                class=${classMap({ hidden: this.activeTab !== 'Recurring' })}
                .isRecurring=${true}
                .open=${this.filterSheetOpen}
                .loading=${this.loading}
                .wireCompanies=${this.wireCompanies}
                .debitAccounts=${this.debitWireAccounts as WireAccount[]}
                @close=${() => {
                    this.filterSheetOpen = false;
                }}
                @applyFilters=${({ detail }: CustomEvent) => {
                    const { recurringDto } = detail;
                    if (!deepEquals(this.recurringFilters, recurringDto)) {
                        this.fetchWirePayments('Recurring', recurringDto);
                    }
                }}
            ></wire-payments-filter-sheet>

            ${this.wireFileFeatureFlagEnabled
                ? html`<wire-file-filter-sheet
                      class=${classMap({ hidden: this.activeTab !== 'WireFile' })}
                      .open=${this.filterSheetOpen}
                      .loading=${this.loading}
                      @close=${() => {
                          this.filterSheetOpen = false;
                      }}
                      @applyFilters=${({ detail }: CustomEvent) => {
                          if (!deepEquals(this.wireFileFilters, detail)) {
                              this.fetchWireFileActivity(detail);
                          }
                      }}
                  ></wire-file-filter-sheet>`
                : nothing}`;
    }

    private configureFooter() {
        if (!this.isDesktop) {
            this.footerService.hide();
            return;
        }
        this.footerService.configure({
            direction: 'row',
            buttonConfig: [
                {
                    success: true,
                    text: 'Approve',
                    disabled: this.selectedWires.length === 0,
                    onClick: () => {
                        this.showApproveReject('approve');
                    },
                },
                {
                    error: true,
                    text: 'Reject',
                    disabled: this.selectedWires.length === 0,
                    onClick: () => {
                        this.showApproveReject('reject');
                    },
                },
            ],
        });
    }

    private async showApproveReject(action: 'approve' | 'reject') {
        const actionText = action === 'approve' ? 'Approve' : 'Reject';
        const { reason } = await this.dialogService.open(
            html`<div>Are you sure you want to ${action} the selected wire payments?</div>
                <br /><tm-text-field
                    label="Comments"
                    @value-changed=${(e: CustomEvent) => {
                        const { value } = e.detail;
                        this.comments = value;
                    }}
                ></tm-text-field>`,
            `${actionText} Wire Payments`,
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Confirm',
                    },

                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                    },
                },
            }
        );
        if (reason === 'confirm') {
            if (action === 'approve') {
                await this.tryFetch(
                    () =>
                        Promise.all([
                            this.wireService.batchApproveOrReject(
                                {
                                    id: 0,
                                    updatedDate: '',
                                    lookups: this.selectedWires.map(w => ({
                                        key: w.id ? w.id.toString() : '',
                                        value: this.comments,
                                        id: 0,
                                        updatedDate: '',
                                    })),
                                },
                                action
                            ),
                        ]),
                    () => {
                        this.notificationService.renderSuccess(
                            `Wire payments ${action}d successfully`
                        );
                        this.selectedWires = [];
                        this.comments = '';
                        this.fetchWirePayments(
                            this.activeTab as 'OneTime' | 'Recurring',
                            this.activeFilters as WiresFilter
                        );
                    },
                    () => this.notificationService.renderError(`Failed to ${action} wire payments`)
                );
            }
            if (action === 'reject') {
                await this.tryFetch(
                    () =>
                        Promise.all([
                            this.wireService.batchApproveOrReject(
                                {
                                    id: 0,
                                    updatedDate: '',
                                    lookups: this.selectedWires.map(w => ({
                                        key: w.id ? w.id.toString() : '',
                                        value: this.comments,
                                        id: 0,
                                        updatedDate: '',
                                    })),
                                },
                                action
                            ),
                        ]),
                    () => {
                        this.notificationService.renderSuccess(
                            `Wire payments ${action}ed successfully`
                        );
                        this.selectedWires = [];
                        this.comments = '';
                        this.fetchWirePayments(
                            this.activeTab as 'OneTime' | 'Recurring',
                            this.activeFilters as WiresFilter
                        );
                    },
                    () => this.notificationService.renderError(`Failed to ${action} wire payments`)
                );
            }
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WiresContainer;
    }
}
